import { SorterResult } from 'antd/es/table/interface';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fieldListStatusMap } from '../components/AdminPage/AdminPageTablesButton';
import { PaginatedTabExtendsType } from '../containers/Pages/AdminPage/TabsContainer';
import { localizedSearchTermForApi } from '../utils/helpers';
import useDebounce from './useDebounce';

export const generateFullPaginatedRequestUrl = (
    endpoint: string,
    params?: {
        size?: number;
        page?: number;
        isActive?: boolean;
        sort?: string;
        searchTerm?: string;
        isFullState?: boolean;
    }
): string => {
    return `${endpoint}${
        params
            ? `?${params?.isFullState ? '' : params?.isActive ? 'active' : 'inactive'}${
                  params?.size ? `&size=${params?.size}` : ''
              }${params?.page ? `&page=${params?.page}` : ''}${params?.sort ? `&sort=${params?.sort}` : ''}${
                  params?.searchTerm ? `&searchTerm=${localizedSearchTermForApi(params?.searchTerm)}` : ''
              }`
            : ''
    }`;
};

export const MIN_CHARS_TO_SEARCH = 3;

const defaultAllowedSortFieldNames = ['name'];
const defaultAllowedSortDirections = ['asc', 'desc'];
const useAdminPagesPaginatedUtils = <T extends PaginatedTabExtendsType>(
    nestedPage: string = '',
    allowedSortFieldNames: string[] = defaultAllowedSortFieldNames,
    allowedSortDirections: string[] = defaultAllowedSortDirections
) => {
    const {
        query: { page, size, active, sort, adminOption },
        push
    } = useRouter();
    const { t } = useTranslation('common');

    const sourceKeyFromPath = adminOption?.toString() + nestedPage;
    const sortingParamFieldName = allowedSortFieldNames.includes(sort?.toString().split(',')[0])
        ? sort.toString().split(',')[0]
        : allowedSortFieldNames[0];

    const sortingParamDirection = allowedSortDirections.includes(sort?.toString().split(',')[1])
        ? sort.toString().split(',')[1]
        : allowedSortDirections[0];
    const sortOrder = sortingParamDirection === allowedSortDirections[0] ? 'ascend' : 'descend';
    const isActive = +active !== 1 ? false : true;
    const activeKey = +active !== 1 ? fieldListStatusMap.inactive : fieldListStatusMap.active;
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const errorMessage =
        debouncedSearchTerm && debouncedSearchTerm?.length < MIN_CHARS_TO_SEARCH ? t('searchTermTooShort') : null;

    useEffect(() => {
        // if page is not 1 and debouncedSearchTerm is not null, reset page to 1. we just need to check if the debouncedSearchTerm has changed
        if (debouncedSearchTerm?.length >= MIN_CHARS_TO_SEARCH && +page !== 1) {
            handleResetPage();
        }
    }, [debouncedSearchTerm]);

    const getRequestParams = (isFullState: boolean = false) => {
        return {
            size: +size,
            page: +page - 1,
            isActive,
            sort: `${sortingParamFieldName},${sortingParamDirection}`,
            searchTerm: debouncedSearchTerm?.length < 3 ? null : debouncedSearchTerm,
            isFullState
        };
    };

    const pushActivePath = (key: string) => {
        return push(
            `/admin/${sourceKeyFromPath}?size=${+size}&page=1&active=${key}&sort=${sortingParamFieldName},${sortingParamDirection}`
        );
    };

    const pushSelectedRow = (id: number) => {
        return push(`/admin/${sourceKeyFromPath}/${id}`);
    };

    const handlePagination = (pagination, _, sorter) => {
        if ('pageSize' in pagination)
            push(
                `/admin/${sourceKeyFromPath}?size=${pagination.pageSize}&page=${pagination.current}${
                    +active ? `&active=${+active}` : ''
                }${
                    (sorter as SorterResult<T>).column && (sorter as SorterResult<T>).columnKey
                        ? `&sort=${(sorter as SorterResult<T>).columnKey},${
                              (sorter as SorterResult<T>).order === 'ascend' ? 'asc' : 'desc'
                          }`
                        : `&sort=${sortingParamFieldName},${sortingParamDirection}`
                }`
            );
    };

    const handleResetPage = () => {
        push(
            `/admin/${sourceKeyFromPath}?size=${+size}&page=1&active=${+active}&sort=${sortingParamFieldName},${sortingParamDirection}`
        );
    };

    return {
        page: +page,
        size: +size,
        active: +active,
        isActive,
        activeKey,
        sortingParamFieldName,
        sortingParamDirection,
        sortOrder: sortOrder as 'ascend' | 'descend',
        searchTerm,
        debouncedSearchTerm,
        errorMessage,
        setSearchTerm,
        getRequestParams,
        pushActivePath,
        pushSelectedRow,
        handlePagination,
        handleResetPage
    };
};

export default useAdminPagesPaginatedUtils;
